// cSpell:ignore privé Samenvatting
// The figures the summary's cards and the cost drawer both read (moved out
// of Step4Summary so the drawer does not import the step): each category's
// VAT and period (buildCatMeta), what a tenant owes incl. VAT
// (tenantOwedInclVat), and how a confirmed settlement split each category
// (confirmedCardDistribution).

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { isPerOccupant } from "../../../../engine";
import {
  categorySpecOf,
  settledKeyName,
} from "../../../../settlementView.utils";
import {
  ApportionmentKey,
  Category,
  DistributionMethod,
  Invoice,
  isDepartureSettlement,
  methodLabel,
  PerContractResult,
  Settlement,
} from "../../../../types";
import { btwOf } from "./shared";
import { AssignableProperty } from "./types";

// The internal marker of a unit's own costs in a breakdown key.
export const PRIVATE_SUFFIX = " (privé)";

// Per byCat key: the effective VAT rate (to derive btw on a tenant's share) and
// the combined period of that category's invoices.
export interface CatMeta {
  excl: number;
  btw: number;
  from: string;
  to: string;
  descriptions: string[];
  suppliers: string[];
  // What the broker specified for the category's "Other" costs.
  categorySpecs: string[];
  // Real invoice ids feeding this category, used to open the cost-detail
  // panel from a card's invoice line.
  invoiceIds: string[];
  // The own period of each of those costs: each is split over the units that
  // existed when it began (see keySplitsForCosts).
  costPeriods: { from: string; to: string }[];
  // The costs themselves, for the invoice lines a card's category row opens.
  costs: Invoice[];
}

// A cost's breakdown key: its category, with the private marker for a
// unit's own cost.
export const byCatKey = (inv: Invoice): string =>
  inv.scope === "private" ? `${inv.category}${PRIVATE_SUFFIX}` : inv.category;

// Effective VAT rate + period per category from an already-selected cost list.
// Shared between the wizard's Samenvatting (live draft) and the ConfirmedView
// (frozen snapshot), so both render the exact same cards.
export const buildCatMeta = (selected: Invoice[]): Record<string, CatMeta> => {
  const out: Record<string, CatMeta> = {};
  for (const inv of selected) {
    const key = byCatKey(inv);
    const m =
      out[key] ??
      (out[key] = {
        excl: 0,
        btw: 0,
        from: inv.periodFrom,
        to: inv.periodTo,
        descriptions: [],
        suppliers: [],
        categorySpecs: [],
        invoiceIds: [],
        costPeriods: [],
        costs: [],
      });
    m.excl += inv.amountExclVat;
    m.btw += btwOf(inv);
    if (inv.periodFrom < m.from) m.from = inv.periodFrom;
    if (inv.periodTo > m.to) m.to = inv.periodTo;
    const desc = inv.description?.trim();
    if (desc && !m.descriptions.includes(desc)) m.descriptions.push(desc);
    const supplier = inv.supplier?.trim();
    if (supplier && !m.suppliers.includes(supplier)) {
      m.suppliers.push(supplier);
    }
    const spec = categorySpecOf(inv);
    if (spec && !m.categorySpecs.includes(spec)) m.categorySpecs.push(spec);
    m.invoiceIds.push(inv.id);
    m.costPeriods.push({ from: inv.periodFrom, to: inv.periodTo });
    m.costs.push(inv);
  }
  return out;
};

// What a tenant actually owes, INCLUDING VAT. `PerContractResult.balance` is
// excl. btw (the engine distributes settlementAmount()), while an invitation
// bills the VAT-inclusive figure. The wizard, the confirmed view and the
// statement all have to bill the SAME number, so it is derived here once.
export const tenantOwedInclVat = (
  pc: PerContractResult,
  catMeta: Record<string, CatMeta>,
): { incl: number; btw: number } => {
  let totExcl = 0;
  let totBtw = 0;
  for (const [key, excl] of Object.entries(pc.breakdown)) {
    const meta = catMeta[key];
    const rate = meta && meta.excl > 0 ? meta.btw / meta.excl : 0;
    totExcl += excl;
    totBtw += excl * rate;
  }
  // The engine sums VAT per cost at that cost's own rate; the category
  // average above is only a fallback for snapshots frozen before costVat
  // existed. Two rates inside one category (21% + 6% maintenance) make the
  // average bill one tenant too little and the other too much.
  if (pc.costVat != null) {
    totBtw = pc.costVat;
  }
  const invoices = +(totExcl + totBtw).toFixed(2);
  const incl = +(invoices - pc.provision).toFixed(2);
  const btw = invoices !== 0 ? +((incl * totBtw) / invoices).toFixed(2) : 0;
  return { incl, btw };
};

// How a group category was split: its key, or a method's label for a
// reading or a metering company's amount; `perOccupant` when the figures
// were entered per tenant (then no occupied-days share applies).
export interface CategoryDistribution {
  key?: ApportionmentKey;
  label?: string;
  // A reading or a metering company's amount: which one, for a reading's
  // share ("120/900 kWh").
  method?: DistributionMethod;
  perOccupant?: boolean;
  // A reading's or a metering company's category: the key that split the
  // owners' part over the units (see ownerPartKeyName), for an owner's
  // share of it.
  ownerPartKey?: ApportionmentKey;
}

// What a card needs to say how each category was split: the split per
// breakdown key, and the group's units for a key's share and its hover.
export interface ICardDistribution {
  byCat: Record<string, CategoryDistribution>;
  units: { id: string; code: string; address?: string; archivedOn?: string }[];
  // When `units` holds only some of the group's units: every unit of the
  // group without its label (`allUnits`), or on older snapshots their number.
  allUnits?: { id: string; archivedOn?: string }[];
  unitCount?: number;
}

// The confirmed view's distribution, from the frozen snapshot: the units
// frozen on confirm (archiving or renaming one later changes neither the
// labels nor the equal key's "1/n"; older snapshots read the live property
// list), and each common category's key with the weights frozen into the
// snapshot, so an edited (or deleted) key never rewrites a confirmed
// settlement, or the method's label.
export const confirmedCardDistribution = (params: {
  settlement: Settlement;
  costs: Invoice[];
  keys: ApportionmentKey[];
  assignable: AssignableProperty[];
  // A departure settlement of a unit in a building: the building.
  departureGroupId?: string;
}): ICardDistribution => {
  const { settlement: s, costs, keys, assignable, departureGroupId } = params;
  const { snapshot } = s;
  const groupId = s.groupId ?? departureGroupId;
  const groupUnits = (): ICardDistribution["units"] =>
    snapshot?.groupUnits?.length
      ? snapshot.groupUnits.map(u => ({
          id: String(u.id),
          code: u.label,
          address: u.label,
          archivedOn: u.archivedOn,
        }))
      : assignable
          .filter(p => p.parentId === groupId)
          .map(p => ({ id: p.id, code: p.code, address: p.address }));
  const units = s.scope !== "group" && !departureGroupId ? [] : groupUnits();
  const byCat: Record<string, CategoryDistribution> = {};
  const selected = snapshot?.selectedKeyId ?? {};
  const frozen = snapshot?.keySnapshots ?? {};
  const isDeparture = isDepartureSettlement(s);
  // A key as the settlement froze it: its weights and name on confirm.
  const settledKey = (keyId: string, category: Category): ApportionmentKey => {
    const live = keys.find(k => k.id === keyId);
    return {
      id: keyId,
      groupId: groupId ?? "",
      name: settledKeyName({
        snapshot,
        keyId,
        category,
        liveName: live?.name,
        deletedName: getLocalizedText("costs.keys.deleted_name"),
      }),
      isSystem: live?.isSystem ?? false,
      isEqual: live?.isEqual ?? false,
      weights: frozen[keyId] ?? live?.weights ?? {},
      createdAt: live?.createdAt ?? "",
      updatedAt: live?.updatedAt ?? "",
    };
  };
  const equalKeyId = keys.find(k => k.isSystem && k.isEqual)?.id;
  for (const inv of costs) {
    if (inv.scope !== "common") continue;
    const method = snapshot?.distributionMethod?.[inv.category];
    if (method === "consumption" || method === "company") {
      // The owners' part follows the category's key, or the equal split
      // when none was chosen (see ownerPartKeyName).
      const ownerPartKeyId = selected[inv.category] ?? equalKeyId;
      byCat[inv.category] = {
        label: methodLabel(method, inv.category, { isDeparture }),
        method,
        perOccupant: isPerOccupant(
          (method === "consumption"
            ? snapshot?.consumption?.[inv.category]
            : snapshot?.companyAmounts?.[inv.category]) ?? {},
        ),
        ownerPartKey: ownerPartKeyId
          ? settledKey(ownerPartKeyId, inv.category)
          : undefined,
      };
      continue;
    }
    const keyId = selected[inv.category];
    if (!keyId) continue;
    byCat[inv.category] = { key: settledKey(keyId, inv.category) };
  }
  return {
    byCat,
    units,
    allUnits: snapshot?.allGroupUnits,
    unitCount: snapshot?.groupUnitCount,
  };
};
